/**
 * Version Details form's option lists (Qualification Co-ordinator / SOR Template /
 * Override Brand), plus the Modules table's NetSuite product lookup, with React Query.
 * All four are rarely-changing reference/dropdown data, so they use the LOOKUP cache
 * profile (unlike QUERY_CACHE_PROFILES.TABLE, used for the actual paginated rows).
 */
import { getQualificationCoordinatorOptions, GetListofModule } from '../../services/ProgramService';
import { GetStatemantTmpList } from '../../services/GradeSettingService';
import { GetListOfBrandSettingCancelToken } from '../../services/BrandServices';
import { GetNetsuiteItemsCancelToken } from '../../services/CourseService';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

/**
 * Version Details - Qualification Co-ordinator options.
 * @returns {{ options: Array<{value, label}>, isLoading: boolean }}
 */
export function useCoordinatorOptions() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionDetails.root, QUERY_KEYS.versionDetails.coordinatorOptions],
        async () => {
            const res = await getQualificationCoordinatorOptions();
            return res.data?.getQualificationCoordinatorOptions || [];
        },
        { cacheProfile: QUERY_CACHE_PROFILES.LOOKUP }
    );

    return { options: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Version Details - SOR Template options.
 * @returns {{ options: Array<{value, label}>, isLoading: boolean }}
 */
export function useSorTemplateOptions() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionDetails.root, QUERY_KEYS.versionDetails.sorTemplateOptions],
        async () => {
            const res = await GetStatemantTmpList({ page: 1, limit: 1000, search: "", key: "name", sort: "ASC", exportStatus: "true" });
            const rows = res.data?.result || [];
            return rows.map((row) => ({ value: row.id, label: row.name }));
        },
        { cacheProfile: QUERY_CACHE_PROFILES.LOOKUP }
    );

    return { options: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Version Details - Override Brand options.
 * @returns {{ options: Array<{value, label}>, isLoading: boolean }}
 */
export function useBrandOptions() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionDetails.root, QUERY_KEYS.versionDetails.brandOptions],
        async () => {
            const res = await GetListOfBrandSettingCancelToken();
            const rows = res.data?.brand_setting_list || [];
            return rows.map((row) => ({ value: row.id, label: row.brand_name }));
        },
        { cacheProfile: QUERY_CACHE_PROFILES.LOOKUP }
    );

    return { options: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Version Modules table - full NetSuite item list, resolved client-side against each
 * row's NetSuite product id (the module row only carries the id, not its display name).
 * @returns {{ items: Array, isLoading: boolean }}
 */
export function useNetsuiteItems() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionModules.root, QUERY_KEYS.versionModules.netsuiteItems],
        async () => {
            const res = await GetNetsuiteItemsCancelToken("All");
            return res?.data?.data?.items || [];
        },
        { cacheProfile: QUERY_CACHE_PROFILES.LOOKUP }
    );

    return { items: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Version Modules table - Pre-Requisites column's id/name source list, covering every
 * module in the system (not just this version's), since a saved prerequisite can belong
 * to a different programme/version than the one currently being viewed. Returned as a
 * plain array rather than a Map since the query cache is persisted through JSON, which
 * would silently flatten a Map to `{}` on rehydration.
 * @returns {{ modules: Array<{ id: number, name: string }>, isLoading: boolean }}
 */
export function useAllModulesNameById() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionModules.root, QUERY_KEYS.versionModules.allModulesLookup],
        async () => {
            const res = await GetListofModule();
            return (res?.data || []).map((mod) => ({ id: mod.Id, name: mod.Portal }));
        },
        { cacheProfile: QUERY_CACHE_PROFILES.LOOKUP }
    );

    return { modules: query.data ?? [], isLoading: query.isLoading };
}
